import { Button, ButtonProps } from '@chegg-ui/button'
import { SecondaryButton } from '@chegg-ui/button'
import { CheckmarkIcon, spacing, UndoIcon, visuallyHiddenCss } from '@chegg-ui/theme'
import { ListItem, UnorderedList } from '@src/components/List'
import media from '@src/styled/media'
import styled from 'styled-components'
import { UnstyledButton } from '../base/Buttons'
import { DynamicFlex } from '../DynamicFlex'
import * as Types from './types'
import { getShadowColors } from './utils'

export const VisuallyHidden = styled.span`
  ${visuallyHiddenCss}
`

export const CardContent = styled.div<Types.CardContentProps>`
  position: relative;
  border-radius: 4px;
  min-width: 270px;
  width: 100%;
  margin: 0px 0px 5px;
  max-width: 320px;
  padding-bottom: ${({ theme, isNewOrRead, showCtaBanner }) =>
    showCtaBanner ? '0px' : !isNewOrRead ? `60px` : theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.white};
  box-sizing: border-box;
  border-top-left-radius: ${props => props.isNewOrRead && `4px`};
  border-top-right-radius: ${props => !props.isNewOrRead && `4px`};
  box-shadow: ${({ focused, focusedComponent, cardState }) =>
    `inset 0 ${
      focused && focusedComponent === 'editor' ? -7 : -2
    }px 0 0 ${getShadowColors(cardState)}, 0 2px ${
      focused && focusedComponent ? 12 : 7
    }px 0 rgba(0,0,0,0.5);`};
  transition: opacity 0.2s linear;
  &:hover {
    box-shadow: ${({ cardState }) =>
      `inset 0 -7px 0 0 ${getShadowColors(cardState)}, 0 2px 12px 0 rgba(0,0,0,0.5)`};
  }
`
export const CardContentFanta = styled.div<Types.CardContentProps>`
  position: relative;
  border-radius: unset;
  min-width: 270px;
  width: 100%;
  margin: 0px 0px 5px;
  max-width: 320px;
  background-color: ${({ theme }) => theme.colors.white};
  box-sizing: border-box;
  box-shadow: ${props =>
    `0 0 ${
      props.focused && props.focusedComponent === 'editor' ? 8 : 4
    }px rgba(34, 34, 34, 0.24)`};
  transition: opacity 0.2s linear;
  &:hover {
    box-shadow: 0 0 8px rgba(34, 34, 34, 0.4);
  }
  border: solid 1px ${({ theme }) => theme.colors.greyLight};
`

export const InnerContent = styled.div`
  padding-left: 17px;
  padding-top: ${({ theme }) => theme.spacing.lg};
`
export const InnerContentFlex = styled(DynamicFlex)`
  padding: ${spacing.px16};
`

export const OuterWrapper = styled.div`
  display: flex;
`

export const CardContainer = styled.div<Types.ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  border-top-left-radius: ${props => !props.isNewCard && `4px`};
  border-top-right-radius: ${props => !props.isNewCard && `4px`};
  margin-top: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  margin-left: 2px;
  margin-right: 2px;
  box-sizing: border-box;
  height: fit-content;
  width: 300px;
  border-radius: 4px;
  ${media.phone`
        justify-content: center; 
        width: 100vw; 
    `};
`

export const CardContainerFanta = styled(CardContainer)`
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-radius: 0;
  margin-top: 7px;
  margin-bottom: ${spacing.px4};
  margin-left: 0;
  margin-right: 0;
`

export const BodyContainer = styled.div`
  display: flex;
  word-break: break-word;
  margin-top: ${({ theme }) => theme.spacing.md2};
`

export const ErrorTextLarge = styled.span`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.font.text.size.lg};
  line-height: 1;
`

export const ErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.px4};
`

export const ArrowContainer = styled.span`
  > svg {
    margin: 0 ${spacing.px8};
  }
`

export const Container = styled.div``
export const TrashContainerCTA = styled.div<{ trashIconSize: number }>`
  cursor: pointer;
  width: ${props => props.trashIconSize}px;
  height: ${props => props.trashIconSize}px;
  ${({ theme }) => toolTipStyle(theme.colors.dark, theme.font.text.size.sm)};
`
export const TrashButton = styled(UnstyledButton)`
  transition: background-color 0.3s ease-in;
  border-radius: 50%;
  height: 32px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.skyLight};
  }
`

export const ContentContainer = styled(DynamicFlex)`
  flex-wrap: wrap;
  margin-bottom: ${spacing.px8};
`

export const CommentText = styled.span`
  font-size: ${({ theme }) => theme.font.text.size.xs};
  color: ${({ theme }) => theme.colors.grey};
`

export const ReplacementList = styled(UnorderedList)`
  display: flex;
  flex-wrap: wrap;
`
export const ReplacementListItem = styled(ListItem)`
  margin-bottom: ${spacing.px4};
  > li + li {
    padding-left: ${spacing.px4};
  }
  padding-right: ${spacing.px4};
`
export const ReplacementButtonStyle = styled(Button).attrs({
  /*
     By default, loading text duplicates button text in a hidden span
     making getByText fail for any button text
     Will be fixed in chegg ui by https://chegg.atlassian.net/browse/CUI-878
  */
  loadingText: ' ',
  size: 'medium',
})<
  ButtonProps & {
    isParaphrase?: boolean
  }
>`
  border: 0;

  > span > span:first-child {
    /* Override Chegg UI button to handle white space normally */
    white-space: normal;

    /* Override Chegg UI button to allow height to grow w/ text */
    height: auto;

    /*
      According to Chegg UI, all buttons should be at least 32px wide
      Currently this is enforced by the loading SVG, but we are removing
      the SVG, so we should enforce this on our own
      Will be fixed in chegg ui by https://chegg.atlassian.net/browse/CUI-878
    */
    min-width: 2rem;
  }

  /*
     Hide loading svg and text
     Will be fixed in chegg ui by https://chegg.atlassian.net/browse/CUI-878
  */
  > span > span:not(:first-child) {
    display: none;
  }

  &,
  &:hover {
    background-color: ${({ theme }) => theme.colors.sky};
    border-color: ${({ theme }) => theme.colors.sky};
  }

  text-align: left;

  ${props => {
    if (props.isParaphrase) {
      return `
        border-radius: 20px;
        padding: 16px;`
    }
    return ``
  }}

  &:disabled {
    background-color: ${({ theme }) => theme.colors.greyMedlight};
    color: ${({ theme }) => theme.colors.grey};
  }
`
export const ReplacementButtonWrapper = styled.div`
  word-break: break-word;
  ${({ theme }) => toolTipStyle(theme.colors.dark, theme.font.text.size.sm)};
`

export const ToolTipText = styled.div``
const toolTipStyle = (bg: string, fontSize: string) => `
  & div[role='tooltip'] {
    background-color: ${bg};
    font-size: ${fontSize};
    padding: 0.3rem;
    top: calc(100% + 0.5rem + 2px);
    box-shadow: unset;
    animation: none;
    width: max-content;
  }

  & div[role='tooltip']:before {
    z-index: -1;
  }
  & div[role='tooltip']:after {
    z-index: -1;
  }
`

export const CommentTextContainer = styled.div<{ addBottomMargin: boolean }>`
  margin-bottom: ${({ addBottomMargin }) => addBottomMargin && spacing.px12};
`

export const OwlRuleContainer = styled.div`
  display: flex;
  margin-top: ${spacing.px12};
  font-size: ${({ theme }) => theme.font.text.size.sm};
`

export const OwlImage = styled.img`
  display: inline-block;
  height: 20.94px;
  margin-right: ${spacing.px4};
`
export const OwlRuleAnchor = styled.a`
  color: ${({ theme }) => theme.colors.link};
  display: inline-block;
  padding-top: ${spacing.px4};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  :first-letter {
    text-transform: uppercase;
  }
  :hover {
    text-decoration: underline;
  }
`

export const UndoReplacementContainer = styled.div`
  margin-left: 0;
  margin-right: 0;
  margin-bottom: ${spacing.px4};
  box-sizing: border-box;
  height: fit-content;
`

export const UndoReplacementBody = styled.div<{ isAccepted: boolean }>`
  display: flex;
  min-width: 270px;
  width: 100%;
  max-width: 320px;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.px8} 0;
  font-size: ${({ theme }) => theme.font.text.size.md};
  background-color: ${props =>
    props.isAccepted ? props.theme.colors.tealXlight : props.theme.colors.greyLight};
`

export const UndoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.link};
  margin-right: ${spacing.px4};
`

export const UndoSecondaryButton = styled(SecondaryButton).attrs({
  /*
     By default, loading text duplicates button text in a hidden span
     making getByText fail for any button text
     Will be fixed in chegg ui by https://chegg.atlassian.net/browse/CUI-878
  */
  loadingText: ' ',
})`
  font-size: ${({ theme }) => theme.font.text.size.sm};
  text-transform: capitalize;
  padding: 0;
`

export const StyledUndoIcon = styled(UndoIcon)`
  height: 20px;
  width: 20px;
  margin: 2px;
`

export const UndoTitleContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.dark};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  padding-left: ${spacing.px12};
`

export const StyledSuccessIcon = styled(CheckmarkIcon)`
  height: 20px;
  width: 20px;
  color: ${({ theme }) => theme.colors.teal};
  margin: 6px 8px 8px 4px;
`

export const UndoTitleText = styled.span``

export const UndoActionContainer = styled.div`
  display: flex;
`

export const UndoClose = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: ${spacing.px8};
`

export { DeleteIcon } from '@chegg-ui/theme'



// WEBPACK FOOTER //
// ./src/components/Cards/styles.tsx